import { instantiate as swiftInstantiate } from "virtual:key-store-swift-client-core";

declare module "virtual:key-store-swift-client-core" {
  export const instantiate: SwiftInstantiate | null;
}

type SwiftCoreExports = {
  keyStoreDeviceBootstrapStorageKey?: () => string;
  keyStoreBrowserUnlockModeCode?: (passkeyPRF: number, pin: number) => number;
  keyStoreBrowserUnlockSecurityLevelCode?: (passkeyPRF: number, pin: number) => number;
  __wasm?: RawSwiftWasmExports;
};

export type DeviceBootstrapAppAttest = {
  key_id?: string;
  team_id?: string;
  bundle_id?: string;
  environment?: "development" | "production";
  challenge?: string;
};

export const deviceBootstrapStorageKey = "key.store.device.bootstrap";

export type BrowserUnlockMode =
  | "unsupported"
  | "passkeyPRF"
  | "nativeAppPasskey"
  | "browserDevicePIN"
  | "pinOnly";

export type BrowserUnlockSecurityLevel =
  | "unavailable"
  | "reducedPIN"
  | "browserBound"
  | "passkeyBound"
  | "nativeHardware";

export type BrowserUnlockWarning =
  | "none"
  | "pinReducedSecurity"
  | "browserMemoryExposure"
  | "passkeyUnavailable"
  | "nativeBridgeRequired";

export type BrowserUnlockRateLimitScope = "none" | "localAdvisory" | "ceremonyBound" | "nativeOrHardware";

export type BrowserUnlockCapabilities = {
  hasWebAuthn: boolean;
  hasPasskeyPRF: boolean;
  hasNonExtractableCryptoKey: boolean;
  hasIndexedDB: boolean;
  hasNativeAppBridge: boolean;
  hasPIN: boolean;
  hasBluetoothHybrid: boolean;
  isKaiOS: boolean;
};

export type BrowserUnlockPlan = {
  mode: BrowserUnlockMode;
  securityLevel: BrowserUnlockSecurityLevel;
  warning: BrowserUnlockWarning;
  rateLimitScope: BrowserUnlockRateLimitScope;
  requiredFactorMask: number;
  sessionTTLSeconds: number;
  maxPINAttempts: number;
  pinLockoutSeconds: number;
};

type SwiftCoreInstance = {
  exports?: SwiftCoreExports;
  instance?: {
    exports?: WebAssembly.Exports;
  };
  swift?: {
    main?: () => void;
  };
};

type SwiftCoreModule = {
  instantiate: SwiftInstantiate;
};

let corePromise: Promise<SwiftCoreExports | null> | null = null;
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

type RawSwiftWasmExports = WebAssembly.Exports & Record<string, unknown>;
type RawSwiftWasmFunction = (...args: number[]) => number;

type SwiftInstantiate = (options: {
  module: Promise<Response> | Response | WebAssembly.Module | BufferSource;
  wasi: ReturnType<typeof createWasi>;
}) => Promise<SwiftCoreInstance>;

function writeU32(memory: WebAssembly.Memory | null, ptr: number, value: number) {
  if (!memory || !ptr) {
    return;
  }
  new DataView(memory.buffer).setUint32(ptr, value, true);
}

function createWasi() {
  let instance: WebAssembly.Instance | null = null;

  const memory = () => (instance?.exports?.memory instanceof WebAssembly.Memory ? instance.exports.memory : null);
  const ok = () => 0;

  return {
    wasiImport: {
      args_get: ok,
      args_sizes_get: (argc: number, argvBufSize: number) => {
        writeU32(memory(), argc, 0);
        writeU32(memory(), argvBufSize, 0);
        return 0;
      },
      clock_res_get: ok,
      clock_time_get: (_clockId: number, _precision: bigint, timePtr: number) => {
        const nanoseconds = Date.now() * 1_000_000;
        const wasmMemory = memory();
        if (wasmMemory) {
          const view = new DataView(wasmMemory.buffer);
          view.setUint32(timePtr, nanoseconds >>> 0, true);
          view.setUint32(timePtr + 4, Math.floor(nanoseconds / 4_294_967_296) >>> 0, true);
        }
        return 0;
      },
      environ_get: ok,
      environ_sizes_get: (count: number, bufSize: number) => {
        writeU32(memory(), count, 0);
        writeU32(memory(), bufSize, 0);
        return 0;
      },
      fd_close: ok,
      fd_fdstat_get: ok,
      fd_prestat_dir_name: ok,
      fd_prestat_get: ok,
      fd_read: ok,
      fd_seek: ok,
      fd_write: (_fd: number, _iovs: number, _iovsLen: number, nwritten: number) => {
        writeU32(memory(), nwritten, 0);
        return 0;
      },
      path_open: ok,
      poll_oneoff: (_input: number, _output: number, _subscriptions: number, events: number) => {
        writeU32(memory(), events, 0);
        return 0;
      },
      proc_exit: (code: number) => {
        throw new Error(`Swift WASI exited with code ${code}`);
      },
      random_get: (ptr: number, len: number) => {
        const wasmMemory = memory();
        if (!wasmMemory) {
          return 1;
        }
        const view = new Uint8Array(wasmMemory.buffer);
        if (globalThis.crypto?.getRandomValues) {
          globalThis.crypto.getRandomValues(view.subarray(ptr, ptr + len));
          return 0;
        }
        for (let index = 0; index < len; index += 1) {
          view[ptr + index] = Math.floor(Math.random() * 256);
        }
        return 0;
      },
    },
    initialize: (wasmInstance: WebAssembly.Instance) => {
      instance = wasmInstance;
    },
    setInstance: (wasmInstance: WebAssembly.Instance) => {
      instance = wasmInstance;
    },
  };
}

function extensionURL(path: string): string | null {
  const runtime = globalThis.chrome?.runtime ?? globalThis.browser?.runtime;
  if (!runtime?.getURL) {
    return null;
  }
  return runtime.getURL(path);
}

function coreBaseURL(): string {
  return extensionURL("key-store-client-core/") ?? new URL("/app/key-store-client-core/", globalThis.location.href).href;
}

export async function loadSwiftClientCore(): Promise<SwiftCoreExports | null> {
  if (!corePromise) {
    corePromise = (async () => {
      if (!swiftInstantiate || !globalThis.WebAssembly || !globalThis.fetch) {
        return null;
      }
      const base = coreBaseURL();
      const module: SwiftCoreModule = { instantiate: swiftInstantiate };
      const result = await module.instantiate({
        module: fetch(new URL("KeyStoreKaiOSWasm.wasm", base).href),
        wasi: createWasi(),
      });
      return result.exports ? { ...result.exports, __wasm: result.instance?.exports } : null;
    })().catch(() => null);
  }
  return corePromise;
}

function rawFunction(wasm: RawSwiftWasmExports, name: string): RawSwiftWasmFunction | null {
  const value = wasm[name];
  return typeof value === "function" ? (value as RawSwiftWasmFunction) : null;
}

function stageString(push: RawSwiftWasmFunction, field: number, value: string | undefined) {
  for (const byte of textEncoder.encode(value ?? "")) {
    push(field, byte);
  }
}

function readRawString(byteAt: RawSwiftWasmFunction, length: number): string | null {
  if (!Number.isInteger(length) || length < 0 || length > 64 * 1024) {
    return null;
  }
  const bytes = new Uint8Array(length);
  for (let index = 0; index < length; index += 1) {
    const byte = byteAt(index);
    if (!Number.isInteger(byte) || byte < 0 || byte > 255) {
      return null;
    }
    bytes[index] = byte;
  }
  return textDecoder.decode(bytes);
}

function isRawJSONStringSafe(value: string | undefined): boolean {
  if (value === undefined) {
    return true;
  }
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code < 0x20 || code === 0x22 || code === 0x5c) {
      return false;
    }
  }
  return true;
}

function isRawDeviceBootstrapSafe(
  platform: string,
  channel: string,
  version: string,
  appAttest?: DeviceBootstrapAppAttest,
): boolean {
  return (
    isRawJSONStringSafe(platform) &&
    isRawJSONStringSafe(channel) &&
    isRawJSONStringSafe(version) &&
    isRawJSONStringSafe(appAttest?.key_id) &&
    isRawJSONStringSafe(appAttest?.team_id) &&
    isRawJSONStringSafe(appAttest?.bundle_id) &&
    isRawJSONStringSafe(appAttest?.environment) &&
    isRawJSONStringSafe(appAttest?.challenge)
  );
}

function intFlag(value: boolean): number {
  return value ? 1 : 0;
}

function browserUnlockArgs(capabilities: BrowserUnlockCapabilities): number[] {
  return [
    intFlag(capabilities.hasWebAuthn),
    intFlag(capabilities.hasPasskeyPRF),
    intFlag(capabilities.hasNonExtractableCryptoKey),
    intFlag(capabilities.hasIndexedDB),
    intFlag(capabilities.hasNativeAppBridge),
    intFlag(capabilities.hasPIN),
    intFlag(capabilities.hasBluetoothHybrid),
    intFlag(capabilities.isKaiOS),
  ];
}

function modeFromCode(code: number): BrowserUnlockMode {
  switch (code) {
    case 10:
      return "passkeyPRF";
    case 20:
      return "nativeAppPasskey";
    case 30:
      return "browserDevicePIN";
    case 40:
      return "pinOnly";
    default:
      return "unsupported";
  }
}

function securityLevelFromCode(code: number): BrowserUnlockSecurityLevel {
  switch (code) {
    case 10:
      return "reducedPIN";
    case 20:
      return "browserBound";
    case 30:
      return "passkeyBound";
    case 40:
      return "nativeHardware";
    default:
      return "unavailable";
  }
}

function warningFromCode(code: number): BrowserUnlockWarning {
  switch (code) {
    case 10:
      return "pinReducedSecurity";
    case 20:
      return "browserMemoryExposure";
    case 30:
      return "passkeyUnavailable";
    case 40:
      return "nativeBridgeRequired";
    default:
      return "none";
  }
}

function rateLimitScopeFromCode(code: number): BrowserUnlockRateLimitScope {
  switch (code) {
    case 10:
      return "localAdvisory";
    case 20:
      return "ceremonyBound";
    case 30:
      return "nativeOrHardware";
    default:
      return "none";
  }
}

function fallbackBrowserUnlockPlan(capabilities: BrowserUnlockCapabilities): BrowserUnlockPlan {
  const hasPIN = capabilities.hasPIN;
  const pinFields = hasPIN
    ? {
        maxPINAttempts: 5,
        pinLockoutSeconds: 15 * 60,
      }
    : {
        maxPINAttempts: 0,
        pinLockoutSeconds: 0,
      };

  if (capabilities.isKaiOS) {
    if (hasPIN) {
      return {
        mode: "pinOnly",
        securityLevel: "reducedPIN",
        warning: "pinReducedSecurity",
        rateLimitScope: "localAdvisory",
        requiredFactorMask: 0b101,
        sessionTTLSeconds: 60,
        ...pinFields,
      };
    }
    return {
      mode: "unsupported",
      securityLevel: "unavailable",
      warning: capabilities.hasWebAuthn ? "nativeBridgeRequired" : "passkeyUnavailable",
      rateLimitScope: "none",
      requiredFactorMask: 0b1,
      sessionTTLSeconds: 0,
      maxPINAttempts: 0,
      pinLockoutSeconds: 0,
    };
  }

  if (capabilities.hasNativeAppBridge) {
    return {
      mode: "nativeAppPasskey",
      securityLevel: "nativeHardware",
      warning: hasPIN ? "pinReducedSecurity" : "none",
      rateLimitScope: "nativeOrHardware",
      requiredFactorMask: hasPIN ? 0b100111 : 0b100011,
      sessionTTLSeconds: 300,
      ...pinFields,
    };
  }
  if (capabilities.hasWebAuthn && capabilities.hasPasskeyPRF) {
    return {
      mode: "passkeyPRF",
      securityLevel: "passkeyBound",
      warning: hasPIN ? "pinReducedSecurity" : "none",
      rateLimitScope: "ceremonyBound",
      requiredFactorMask: hasPIN ? 0b1101 : 0b1001,
      sessionTTLSeconds: 120,
      ...pinFields,
    };
  }
  if (capabilities.hasNonExtractableCryptoKey && capabilities.hasIndexedDB && hasPIN) {
    return {
      mode: "browserDevicePIN",
      securityLevel: "browserBound",
      warning: "browserMemoryExposure",
      rateLimitScope: "localAdvisory",
      requiredFactorMask: 0b111,
      sessionTTLSeconds: 90,
      ...pinFields,
    };
  }
  if (hasPIN) {
    return {
      mode: "pinOnly",
      securityLevel: "reducedPIN",
      warning: "pinReducedSecurity",
      rateLimitScope: "localAdvisory",
      requiredFactorMask: 0b101,
      sessionTTLSeconds: 60,
      ...pinFields,
    };
  }
  return {
    mode: "unsupported",
    securityLevel: "unavailable",
    warning: capabilities.hasWebAuthn ? "nativeBridgeRequired" : "passkeyUnavailable",
    rateLimitScope: "none",
    requiredFactorMask: 0b1,
    sessionTTLSeconds: 0,
    maxPINAttempts: 0,
    pinLockoutSeconds: 0,
  };
}

function rawBrowserUnlockField(
  wasm: RawSwiftWasmExports,
  name: string,
  capabilities: BrowserUnlockCapabilities,
): number | null {
  const fn = rawFunction(wasm, name);
  if (!fn) {
    return null;
  }
  return fn(...browserUnlockArgs(capabilities));
}

export async function browserUnlockPlan(capabilities: BrowserUnlockCapabilities): Promise<BrowserUnlockPlan> {
  const core = await loadSwiftClientCore();
  if (!core?.__wasm) {
    return fallbackBrowserUnlockPlan(capabilities);
  }
  const mode = rawBrowserUnlockField(core.__wasm, "key_store_browser_unlock_mode", capabilities);
  const securityLevel = rawBrowserUnlockField(core.__wasm, "key_store_browser_unlock_security_level", capabilities);
  const warning = rawBrowserUnlockField(core.__wasm, "key_store_browser_unlock_warning", capabilities);
  const factorMask = rawBrowserUnlockField(core.__wasm, "key_store_browser_unlock_factor_mask", capabilities);
  const rateLimitScope = rawBrowserUnlockField(core.__wasm, "key_store_browser_unlock_rate_limit_scope", capabilities);
  const sessionTTL = rawBrowserUnlockField(core.__wasm, "key_store_browser_unlock_session_ttl_seconds", capabilities);
  const maxPINAttempts = rawBrowserUnlockField(core.__wasm, "key_store_browser_unlock_max_pin_attempts", capabilities);
  const pinLockoutSeconds = rawBrowserUnlockField(core.__wasm, "key_store_browser_unlock_pin_lockout_seconds", capabilities);

  if (
    mode === null ||
    securityLevel === null ||
    warning === null ||
    factorMask === null ||
    rateLimitScope === null ||
    sessionTTL === null ||
    maxPINAttempts === null ||
    pinLockoutSeconds === null
  ) {
    return fallbackBrowserUnlockPlan(capabilities);
  }

  return {
    mode: modeFromCode(mode),
    securityLevel: securityLevelFromCode(securityLevel),
    warning: warningFromCode(warning),
    requiredFactorMask: factorMask,
    rateLimitScope: rateLimitScopeFromCode(rateLimitScope),
    sessionTTLSeconds: sessionTTL,
    maxPINAttempts,
    pinLockoutSeconds,
  };
}

export async function deviceBootstrapRequestBody(
  platform: string,
  channel: string,
  version: string,
  appAttest?: DeviceBootstrapAppAttest,
): Promise<string> {
  const core = await loadSwiftClientCore();
  const request = appAttest ? { platform, channel, version, app_attest: appAttest } : { platform, channel, version };
  if (core?.__wasm && isRawDeviceBootstrapSafe(platform, channel, version, appAttest)) {
    const reset = rawFunction(core.__wasm, "key_store_device_bootstrap_reset");
    const push = rawFunction(core.__wasm, "key_store_device_bootstrap_push_utf8_byte");
    const finalize = rawFunction(core.__wasm, "key_store_device_bootstrap_finalize");
    const byteAt = rawFunction(core.__wasm, "key_store_device_bootstrap_result_byte");
    if (reset && push && finalize && byteAt) {
      reset(appAttest ? 1 : 0);
      stageString(push, 0, platform);
      stageString(push, 1, channel);
      stageString(push, 2, version);
      stageString(push, 3, appAttest?.key_id);
      stageString(push, 4, appAttest?.team_id);
      stageString(push, 5, appAttest?.bundle_id);
      stageString(push, 6, appAttest?.environment);
      stageString(push, 7, appAttest?.challenge);
      return readRawString(byteAt, finalize()) ?? JSON.stringify(request);
    }
  }
  return JSON.stringify(request);
}
